import React, { useState, useContext } from 'react';
import useAsyncEffect from 'use-async-effect';
import { useLocation, useHistory } from 'hooks/router';
import AppContext from 'contexts/appContext';
import Inputs from 'components/inputs/inputs';
import Flow from 'components/common/flow/flow';
import Panel from 'components/common/panel/panel';
import Heading from 'components/common/heading/heading';
import { ShowConfirmPopup, ShowNotifyPopup } from 'components/common/popup/popup';
import ErrorItemNotFound from 'components/app/errors/errorItemNotFound';
import SoftwareUpdateDetailsList from './softwareUpdateDetailsList';
import SoftwareUpdateHelper from 'components/navigation/softwareUpdate/softwareUpdateHelper';
import SoftwareUpdateService from 'services/softwareUpdateService';
import Helpers from 'utils/helpers';
import moment from 'moment';
import './softwareUpdateDetailsPage.css';

export default function SoftwareUpdateDetailsPage(props) {
  const { setAppArea, setAppBreadcrumb } = useContext(AppContext);

  const location = useLocation();
  const history = useHistory();
  const id = props.match.params.id;

  const [updateDetails, setUpdateDetails] = useState(null);

  const refetch = async () => {
    setUpdateDetails(await SoftwareUpdateService.API.getSoftwareUpdateDetails(id));
  };

  useAsyncEffect(async () => {
    setAppArea(AppContext.Areas.SoftwareUpdate, 'Update Details');
    setAppBreadcrumb([{ name: 'Home', href: '/' }, { name: 'Software Update', href: '/SoftwareUpdate' }, { name: 'Update Details', selected: true }]);

    await refetch();
  }, [location]);

  const handleCancelPackageClicked = () => {
    ShowConfirmPopup({
      title: 'Confirm',
      message: <div className='py-2'>Are you sure you want to cancel this update?</div>,
      acceptLabel: 'Yes, Cancel',
      acceptIconClass: 'icons8-cancel',
      acceptVariant: 'warning',
      declineLabel: 'No',
      declineIconClass: 'icons8-delete',
      declineVariant: 'default',
      onAccept: () => {
        SoftwareUpdateService.API.cancelSoftwareUpdate(updateDetails.softwareUpdate.softwareUpdateId).then(() => {
          ShowNotifyPopup({
            title: 'Success',
            message: 'Update has been canceled',
            variant: 'success',
            yPosition: 'bottom',
            xPosition: 'center',
            seconds: 5,
          });
          refetch();
        });
      }
    });
  };

  const handleDeletePackageClicked = () => {
    ShowConfirmPopup({
      title: 'Confirm',
      message: <div className='py-2'>Are you sure you want to delete this update?</div>,
      acceptLabel: 'Yes, Delete',
      acceptIconClass: 'icons8-trash',
      acceptVariant: 'danger',
      declineLabel: 'No',
      declineIconClass: 'icons8-delete',
      declineVariant: 'default',
      onAccept: () => {
        SoftwareUpdateService.API.deleteSoftwareUpdate(updateDetails.softwareUpdate.softwareUpdateId).then(() => {
          history.replace('/SoftwareUpdate');
          ShowNotifyPopup({
            title: 'Success',
            message: 'Update has been deleted',
            variant: 'success',
            yPosition: 'bottom',
            xPosition: 'center',
            seconds: 5,
          });
        });
      }
    });
  };

  const renderDistributionDate = () => {
    if (updateDetails.softwareUpdate.distributeOnHold) {
      return null;
    }
    if (updateDetails.softwareUpdate.distributeAsap) {
      return (
        <div className='mb-2'>
          <div><div className='form-label'>Update Date &amp; Time</div></div>
          <div>ASAP</div>
        </div>
      );
    }
    else {
      return (
        <div className='mb-2'>
          <div><div className='form-label'>Update Date &amp; Time</div></div>
          <div>{moment(updateDetails.softwareUpdate.distributeDateTime).format('dddd, MMMM D, YYYY h:mma')}</div>
        </div>
      );
    }
  };

  const renderDistributionOptions = () => {
    if (updateDetails.softwareUpdate.distributeOptionCode !== 'repair') {
      return null;
    }
    return (
      <div className='mb-2'>
        <div><div className='form-label'>QuickMenu</div></div>
        <div><span>Run QM Database Repair After Update</span></div>
      </div>
    );
  };

  if (!updateDetails) {
    //Loading View
    return (
      <>
        <Heading />

        <div className='row justify-content-md-center mx-0 mt-4'>
          <div className='col-12 col-md-10 col-xl-8'>
            <Panel>
              <Panel.SkeletonHeader width={200} />
              <Panel.SkeletonHeader width={400} />
              <Panel.SkeletonBody rows={5} rowHeight={32} rowMargin={14} />
            </Panel>

            <Panel>
              <Panel.SkeletonHeader width={200} />
              <Panel.SkeletonBody rows={8} rowHeight={60} rowMargin={12} />
            </Panel>
          </div>
        </div>
      </>
    );
  }

  if (!updateDetails.softwareUpdate) {
    return <ErrorItemNotFound />;
  }

  return (
    <Flow>
      <Flow.Header>
        <Heading>
          <div className='flex-grow-1 d-flex justify-content-end'>
            {!SoftwareUpdateHelper.canUpdateRefresh(updateDetails.softwareUpdate.statusId) ? null :
              <Inputs.Button variant='default' onClick={refetch} iconClass='icons8-synchronize' label='Refresh' />
            }
            {!SoftwareUpdateHelper.canUpdateEdit(updateDetails.softwareUpdate.statusId) ? null :
              <Inputs.Button variant='info' href={`/SoftwareUpdate/Edit/${id}`} iconClass='icons8-edit' label='Edit' />
            }
            {!SoftwareUpdateHelper.canUpdateCancel(updateDetails.softwareUpdate.statusId) ? null :
              <Inputs.Button variant='warning' onClick={handleCancelPackageClicked} iconClass='icons8-cancel' label='Cancel Update' />
            }
            {!SoftwareUpdateHelper.canUpdateDelete(updateDetails.softwareUpdate.statusId) ? null :
              <Inputs.Button variant='danger' onClick={handleDeletePackageClicked} iconClass='icons8-trash' label='Delete Update' />
            }
          </div>
        </Heading>
      </Flow.Header>

      <Flow.Body>
        <div className='row justify-content-md-center mx-0 mt-4'>
          <div className='col-12 col-md-10 col-xl-8'>
            <Panel>
              <Panel.Header>Update Details</Panel.Header>
              <Panel.Body style={{ backgroundColor: SoftwareUpdateHelper.getUpdateStatusColor(updateDetails.softwareUpdate.statusId) }}>
                <h4 className='text-thin text-white m-0'>
                  {SoftwareUpdateHelper.getUpdateStatusIcon(updateDetails.softwareUpdate.statusId)}
                  <span className='ml-2'>{updateDetails.softwareUpdate.statusName}</span>
                </h4>
              </Panel.Body>
              <Panel.Body>
                <div className='mb-2'>
                  <div><div className='form-label'>Description</div></div>
                  <div>{updateDetails.softwareUpdate.softwareUpdateName}</div>
                </div>
                <div className='mb-2'>
                  <div><div className='form-label'>Software</div></div>
                  <div>{updateDetails.softwareUpdate.softwareName}</div>
                </div>
                <div className='mb-2'>
                  <div><div className='form-label'>Version</div></div>
                  <div>{updateDetails.softwareUpdate.softwareVersionName}</div>
                </div>
                {renderDistributionDate()}
                {renderDistributionOptions()}
                <div className='status small'>
                  <div>Created {moment(updateDetails.softwareUpdate.created).format('dddd, MMMM D, YYYY h:mma')}</div>
                  <div>Modified {moment(updateDetails.softwareUpdate.modified).format('dddd, MMMM D, YYYY h:mma')}</div>
                </div>
              </Panel.Body>
            </Panel>
            <SoftwareUpdateDestinationsPanel refetch={refetch} data={updateDetails.detailsList} />
          </div>
        </div>
      </Flow.Body>
    </Flow>
  );
}

function SoftwareUpdateDestinationsPanel(props) {
  const [filter, setFilter] = useState();
  const [showPending, setShowPending] = useState(true);
  const [showProgress, setShowProgress] = useState(true);
  const [showSuccessful, setShowSuccessful] = useState(true);
  const [showFailed, setShowFailed] = useState(true);

  let activeChildCount = 0; //updateDetails.softwareUpdate.activeChildCount
  let successfulChildCount = 0; //updateDetails.softwareUpdate.successfulChildCount
  let failedChildCount = 0; //updateDetails.softwareUpdate.failedChildCount
  let progressChildCount = 0; //updateDetails.softwareUpdate.progressChildCount

  const data = (() => {
    if (!props.data || props.data.length === 0) {
      return [];
    }
    else {
      return props.data.filter((item) => {
        if (!Helpers.String.isEmpty(filter) &&
          !(item && item.hierarchyName && item.hierarchyName.toLowerCase().indexOf(filter) !== -1)) {
          //hide because doesn't match text filter
          return false;
        }
        switch (item.statusId) {
          case SoftwareUpdateHelper.enums.softwareUpdateDtlStatus.CannotInstall:
            activeChildCount += 1;
            failedChildCount += 1;
            if (!showFailed) return false;
            break;
          case SoftwareUpdateHelper.enums.softwareUpdateDtlStatus.MissingAgent:
            activeChildCount += 1;
            failedChildCount += 1;
            if (!showFailed) return false;
            break;
          case SoftwareUpdateHelper.enums.softwareUpdateDtlStatus.Canceled:
            //always show, regardless of filter types
            break;
          case SoftwareUpdateHelper.enums.softwareUpdateDtlStatus.WaitingToUpdate:
            activeChildCount += 1;
            if (!showPending) return false;
            break;
          case SoftwareUpdateHelper.enums.softwareUpdateDtlStatus.UpdateInProgress:
            activeChildCount += 1;
            progressChildCount += 1;
            if (!showProgress) return false;
            break;
          case SoftwareUpdateHelper.enums.softwareUpdateDtlStatus.UpdateFailed:
            activeChildCount += 1;
            failedChildCount += 1;
            if (!showFailed) return false;
            break;
          case SoftwareUpdateHelper.enums.softwareUpdateDtlStatus.UpdateTimedOut:
            activeChildCount += 1;
            failedChildCount += 1;
            if (!showFailed) return false;
            break;
          case SoftwareUpdateHelper.enums.softwareUpdateDtlStatus.Completed:
            activeChildCount += 1;
            successfulChildCount += 1;
            if (!showSuccessful) return false;
            break;
          case SoftwareUpdateHelper.enums.softwareUpdateDtlStatus.OnHold:
            //always show, regardless of filter types
            break;
          case SoftwareUpdateHelper.enums.softwareUpdateDtlStatus.UpdateTimedOutNeverAttempted:
            activeChildCount += 1;
            failedChildCount += 1;
            if (!showFailed) return false;
            break;
          default:
            break;
        }
        return true;
      });
    }
  })();

  return (
    <Panel>
      <Panel.Header>Sites</Panel.Header>
      <Panel.Body>
        <div className='mb-3'>
          <Inputs.Throttled placeholder='Search' className='input form-control mb-3' onValueChange={(v) => setFilter(v)} />
          <h5>Filter</h5>
          <SoftwareUpdateDetailFilterTag
            title='Pending'
            variant='primary'
            show={showPending}
            count={activeChildCount - successfulChildCount - failedChildCount - progressChildCount}
            onClick={() => setShowPending(!showPending)}
          />
          <SoftwareUpdateDetailFilterTag
            title='In Progress'
            variant='secondary'
            show={showProgress}
            count={progressChildCount}
            onClick={() => setShowProgress(!showProgress)}
          />
          <SoftwareUpdateDetailFilterTag
            title='Successful'
            variant='success'
            show={showSuccessful}
            count={successfulChildCount}
            onClick={() => setShowSuccessful(!showSuccessful)}
          />
          <SoftwareUpdateDetailFilterTag
            title='Failed'
            variant='danger'
            show={showFailed}
            count={failedChildCount}
            onClick={() => setShowFailed(!showFailed)}
          />
        </div>
        <SoftwareUpdateDetailsList onChange={props.refetch} data={data} />
        {/*<SoftwareUpdateDetailsDatatable refetch={props.refetch} data={data} />*/}
      </Panel.Body>
    </Panel>
  );
}

function SoftwareUpdateDetailFilterTag(props) {
  return (
    <span className={`badge badge-lg mb-1 mx-1 badge-light-${props.show ? props.variant : 'default'}`} style={{ cursor: 'pointer' }} onClick={props.onClick}>
      <span className='text-thicker'>{(props.show ? 'Hide ' : 'Show ')}</span> {props.title} ({props.count})
    </span>
  );
}